import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQueries } from '@tanstack/react-query'

import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { Box, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material'

import { BingAdsService, FacebookAdsService, GoogleAdsService } from 'generated'
import useConfig from 'hooks/useConfig'
import useToast from 'hooks/useToast'
import { NavbarBugreport } from 'assets/icons'

function displayName(integration: string): string {
  if (integration === 'Facebook Ads') return 'Meta Ads'
  return integration
}

const Helpdesk = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { workspace } = useConfig()

  // Header renders before a workspace is picked; skip until one is set
  const connectedLists = useQueries({
    queries: [
      {
        queryKey: ['google_ads', workspace],
        queryFn: () => GoogleAdsService.getGoogleAdsConnected(workspace),
        enabled: !!workspace,
      },
      {
        queryKey: ['facebook_ads', workspace],
        queryFn: () => FacebookAdsService.getFacebookAdsConnected(workspace),
        enabled: !!workspace,
      },
      {
        queryKey: ['bing_ads', workspace],
        queryFn: () => BingAdsService.getBingConnected(workspace),
        enabled: !!workspace,
      },
    ],
  })

  const anyQueryError = connectedLists.some(q => q.isError)

  useEffect(() => {
    if (anyQueryError) {
      toast({
        message: 'Fetching Error',
        alertColor: 'error',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyQueryError])

  const isFetchingIntegrations = connectedLists.some(q => q.isLoading)

  const integrationNames = useMemo(() => {
    const google = connectedLists[0].data?.data || []
    const facebook = connectedLists[1].data?.data || []
    const bing = connectedLists[2].data?.data || []

    const names: string[] = []

    if (google.some((item: { auth_status?: string }) => item.auth_status === 'reconnect')) {
      names.push('Google Ads')
    }

    if (facebook.some((item: { auth_status?: string }) => item.auth_status === 'reconnect')) {
      names.push('Facebook Ads')
    }

    if (bing.some((item: { auth_status?: string }) => item.auth_status === 'reconnect')) {
      names.push('Bing Ads')
    }

    return names
  }, [
    connectedLists[0].data,
    connectedLists[1].data,
    connectedLists[2].data,
  ])

  const needsReconnect =
    !isFetchingIntegrations && integrationNames.length > 0
  const labels = integrationNames.map(displayName)

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}>
      {needsReconnect ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            maxWidth: { xs: 220, sm: 480 },
            px: 1.25,
            py: 0.65,
            borderRadius: 2,
            bgcolor: theme => (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
            border: '1px solid',
            borderColor: 'divider',
          }}>
          <WarningAmberOutlinedIcon
            aria-hidden
            color="warning"
            sx={{
              fontSize: 20,
              flexShrink: 0,
              mt: '1px',
            }}
          />
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              lineHeight: 1.4,
              textAlign: 'left',
            }}>
            {labels.length === 1 ? (
              <>
                {labels[0]} needs to be{' '}
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  onClick={() => navigate('/setup/apps')}
                  sx={{
                    verticalAlign: 'baseline',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: 'inherit',
                    cursor: 'pointer',
                    p: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    fontFamily: 'inherit',
                    lineHeight: 'inherit',
                  }}>
                  reconnected
                </Link>
              </>
            ) : (
              <>
                {labels.join(', ')} need to be{' '}
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  onClick={() => navigate('/setup/apps')}
                  sx={{
                    verticalAlign: 'baseline',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: 'inherit',
                    cursor: 'pointer',
                    p: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    fontFamily: 'inherit',
                    lineHeight: 'inherit',
                  }}>
                  reconnected
                </Link>
              </>
            )}
          </Typography>
        </Box>
      ) : null}
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip title="Helpdesk">
          <IconButton
            size="small"
            onClick={() =>
              window.open('https://docs.weberlo.com', '_blank', 'noopener,noreferrer')
            }
            color="primary"
            aria-label="Helpdesk"
            sx={{
              p: 0.5,
              '& svg': { width: 14, height: 17, display: 'block' },
            }}>
            <NavbarBugreport />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  )
}

export default Helpdesk
